import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import channelService from "../../../services/ChannelService";
import { emitCheckoutEvent } from "../../../services/checkoutEvents";
import { markAlreadyPaid, markPaymentFailed } from "../../../sessionCompletion";
import { storage } from "../../../storageconstants";
import { debugLog, debugError } from "../../../utils";

function formatMmSs(totalSec) {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const TIMER_STYLES = {
  normal: {
    row: [
      "bg-thm-body-warning-bg",
      "border-thm-body-warning-border-color",
    ],
    icon: [
      "text-thm-body-warning-text-and-icon-color",
    ],
    text: [
      "text-thm-body-warning-text-and-icon-color",
    ],
  },
  orange: {
    row: [
      "bg-thm-body-warning-bg",
      "border-thm-body-warning-border-color",
    ],
    icon: [
      "text-thm-body-warning-text-and-icon-color",
    ],
    text: [
      "text-thm-body-warning-text-and-icon-color",
    ],
  },
  red: {
    row: [
      "bg-thm-body-error-bg",
      "border-thm-body-error-border-color",
    ],
    icon: [
      "text-thm-body-error-text-and-icon-color",
    ],
    text: [
      "text-thm-body-error-text-and-icon-color",
    ],
  },
};

const TIMER_STYLE_CLASSES = {
  row: Object.values(TIMER_STYLES).flatMap((style) => style.row),
  icon: Object.values(TIMER_STYLES).flatMap((style) => style.icon),
  text: Object.values(TIMER_STYLES).flatMap((style) => style.text),
};

export default class DirectDepositController extends Controller {
  static targets = [
    "activeBlock",
    "qrSection",
    "qrToggle",
    "qrToggleText",
    "qrCode",
    "qrToggleIcon",
    "expiresIn",
    "timerRow",
    "waitingBlock",
    "expiredBlock",
    "spinner",
    "assetIcon",
    "assetHeading",
    "amountWithSymbol",
    "bannerSymbol",
    "bannerNetwork",
  ];

  static values = {
    code: String,
    session: String,
    amount: String,
    channelid: String,
    successRedirectUrl: String,
    failureRedirectUrl: String,
    /** Server-rendered: { wallets, error, chainId } from CreateEphemeralWalletsForDeposit */
    walletsPayload: Object,
    /** Unix seconds when the ephemeral deposit address expires (CreatedOn + TTL from server). 0 = no countdown. */
    expiresAtUnix: Number,
    networkExplorerLink: String,
  };

  connect() {
    this.hideLoadingScreen();
    this._walletExpired = false;

    this.applyDirectTransferSelectionFromStorage();
    this.updateCheckoutOverviewFromCurrentAmount();

    if (this.hasExpiresAtUnixValue && this.expiresAtUnixValue > 0) {
      this.startExpiryCountdown();
    } else if (this.hasTimerRowTarget) {
      this.timerRowTarget.classList.add("hidden");
    }

    // Subscribe to Pusher for real-time deposit events.
    if (this.channelidValue) {
      channelService.connect(this.channelidValue);
      this._onDepositConfirmed = (e) => this.handleDepositConfirmed(e);
      this._onDepositInsufficient = (e) => this.handleDepositInsufficient(e);
      this._onDepositManualCheck = (e) => this.handleDepositManualCheck(e);
      window.addEventListener("dd:deposit-confirmed", this._onDepositConfirmed);
      window.addEventListener("dd:deposit-insufficient", this._onDepositInsufficient);
      window.addEventListener("dd:deposit-manual-check", this._onDepositManualCheck);
    }

    this.initQrState();
  }

  disconnect() {
    if (this._expiryTimer) {
      clearInterval(this._expiryTimer);
      this._expiryTimer = null;
    }
    if (this._onDepositConfirmed) {
      window.removeEventListener("dd:deposit-confirmed", this._onDepositConfirmed);
    }
    if (this._onDepositInsufficient) {
      window.removeEventListener("dd:deposit-insufficient", this._onDepositInsufficient);
    }
    if (this._onDepositManualCheck) {
      window.removeEventListener("dd:deposit-manual-check", this._onDepositManualCheck);
    }
    if (this._qrDesktopMediaQuery && this._onDesktopMediaChange) {
      if (this._qrDesktopMediaQuery.removeEventListener) {
        this._qrDesktopMediaQuery.removeEventListener("change", this._onDesktopMediaChange);
      } else if (this._qrDesktopMediaQuery.removeListener) {
        this._qrDesktopMediaQuery.removeListener(this._onDesktopMediaChange);
      }
    }
  }

  initQrState() {
    if (!this.hasQrCodeTarget) return;
    this._qrDesktopMediaQuery = window.matchMedia("(min-width: 640px)");
    this.setQrExpanded(this._qrDesktopMediaQuery.matches);

    this._onDesktopMediaChange = (event) => {
      this.setQrExpanded(event.matches);
    };
    if (this._qrDesktopMediaQuery.addEventListener) {
      this._qrDesktopMediaQuery.addEventListener("change", this._onDesktopMediaChange);
    } else if (this._qrDesktopMediaQuery.addListener) {
      this._qrDesktopMediaQuery.addListener(this._onDesktopMediaChange);
    }
  }

  setQrExpanded(isExpanded) {
    this.qrCodeTarget.classList.toggle("hidden", !isExpanded);
    this.qrCodeTarget.classList.toggle("flex", isExpanded);

    if (this.hasQrToggleTextTarget) {
      this.qrToggleTextTarget.textContent = isExpanded ? "Hide QR" : "Show QR";
    }
    if (this.hasQrToggleIconTarget) {
      // Chevron down when collapsed (tap to expand), chevron up when expanded (tap to collapse)
      this.qrToggleIconTarget.innerHTML = isExpanded
        ? '<path stroke-linecap="round" stroke-linejoin="round" d="m4.5 15.75 7.5-7.5 7.5 7.5" />'
        : '<path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />';
    }
  }

  startExpiryCountdown() {
    const tick = () => {
      const nowSec = Math.floor(Date.now() / 1000);
      const rem = Math.max(0, this.expiresAtUnixValue - nowSec);
      if (this.hasExpiresInTarget) {
        this.expiresInTarget.textContent = formatMmSs(rem);
      }
      this.updateTimerColorState(rem);
      if (rem <= 0) {
        if (this._expiryTimer) {
          clearInterval(this._expiryTimer);
          this._expiryTimer = null;
        }
        this.showWalletExpired();
      }
    };
    tick();
    this._expiryTimer = setInterval(tick, 1000);
  }

  updateTimerColorState(remainingSeconds) {
    if (!this.hasTimerRowTarget) return;
    const state = remainingSeconds <= 60 ? "red" : remainingSeconds <= 300 ? "orange" : "normal";
    const timerStyle = TIMER_STYLES[state];
    if (!timerStyle) return;

    this.timerRowTarget.classList.remove(...TIMER_STYLE_CLASSES.row);
    this.timerRowTarget.classList.add(...timerStyle.row);

    const iconElement = this.timerRowTarget.querySelector("svg");
    if (iconElement) {
      iconElement.classList.remove(...TIMER_STYLE_CLASSES.icon);
      iconElement.classList.add(...timerStyle.icon);
    }

    const textElement = this.timerRowTarget.querySelector("span.text-sm");
    if (textElement) {
      textElement.classList.remove(...TIMER_STYLE_CLASSES.text);
      textElement.classList.add(...timerStyle.text);
    }
  }

  showWalletExpired() {
    if (this._walletExpired) return;
    this._walletExpired = true;
    if (this.hasActiveBlockTarget) {
      this.activeBlockTarget.classList.add("hidden");
    }
    if (this.hasTimerRowTarget) {
      this.timerRowTarget.classList.add("hidden");
    }
    if (this.hasWaitingBlockTarget) {
      this.waitingBlockTarget.classList.add("hidden");
    }
    if (this.hasExpiredBlockTarget) {
      this.expiredBlockTarget.classList.remove("hidden");
    }
  }

  handleDepositConfirmed(event) {
    const txHash = String(event.detail?.tx_hash || "");
    const amountReceived = String(event.detail?.amount_received || "");
    if (txHash && amountReceived) {
      emitCheckoutEvent("checkout_dd_deposit_detected", {
        tx_hash: txHash,
        amount_received: amountReceived,
      });
    }

    if (this._walletExpired) return;
    debugLog("DirectDeposit: deposit confirmed!", event.detail);
    markAlreadyPaid();
    const successRedirectURL = this.getValidatedRedirectURL(this.successRedirectUrlValue);
    if (successRedirectURL) {
      window.location.assign(successRedirectURL);
      return;
    }

    const txExplorerURL = String(event.detail.tx_explorer_url || "");
    if (txExplorerURL !== "") {
      sessionStorage.setItem(storage.DIRECT_DEPOSIT_EXPLORER_URL, txExplorerURL);
    } else {
      sessionStorage.removeItem(storage.DIRECT_DEPOSIT_EXPLORER_URL);
    }

    const url = `/${this.codeValue}/direct-deposit-success?session=${encodeURIComponent(this.sessionValue)}`;
    Turbo.visit(url, { frame: "step_content", action: "advance" });
  }

  handleDepositInsufficient(event) {
    if (this._walletExpired) return;
    debugLog("DirectDeposit: deposit insufficient!", event.detail);
    this.persistInsufficientResult(event.detail);
    markPaymentFailed();
    const failureRedirectURL = this.getValidatedRedirectURL(this.failureRedirectUrlValue);
    if (failureRedirectURL) {
      window.location.assign(failureRedirectURL);
      return;
    }
    const url = `/${this.codeValue}/direct-deposit-insufficient-funds?session=${encodeURIComponent(this.sessionValue)}`;
    Turbo.visit(url, { frame: "step_content", action: "advance" });
  }

  persistInsufficientResult(payload) {
    if (!payload || typeof payload !== "object") return;
    const requiredAmount = String(payload.required_amount || "").trim();
    const receivedAmount = String(payload.received_amount || "").trim();
    const requiredToken = String(payload.required_token || payload.received_token || "").trim();
    const receivedToken = String(payload.received_token || payload.required_token || "").trim();

    if (!requiredAmount || !receivedAmount) return;

    const result = {
      requiredAmount,
      receivedAmount,
      requiredToken,
      receivedToken,
    };
    sessionStorage.setItem(storage.DIRECT_DEPOSIT_INSUFFICIENT_RESULT, JSON.stringify(result));
  }

  handleDepositManualCheck(event) {
    if (this._walletExpired) return;
    debugLog("DirectDeposit: deposit needs manual check!", event.detail);
    const url = `/${this.codeValue}/direct-deposit-manual-check?session=${encodeURIComponent(this.sessionValue)}`;
    Turbo.visit(url, { frame: "step_content", action: "advance" });
  }

  /** Applies token/network/icon chosen on direct-transfer-options (sessionStorage). */
  applyDirectTransferSelectionFromStorage() {
    const symbol = sessionStorage.getItem(storage.DIRECT_TRANSFER_SYMBOL);
    const network = sessionStorage.getItem(storage.DIRECT_TRANSFER_NETWORK);
    const icon = sessionStorage.getItem(storage.DIRECT_TRANSFER_ICON);
    if (!symbol || !network) return;

    if (this.hasAssetIconTarget) {
      if (icon) this.assetIconTarget.src = icon;
      this.assetIconTarget.alt = symbol;
    }
    if (this.hasAssetHeadingTarget) {
      this.assetHeadingTarget.textContent = `${symbol} (${network})`;
    }
    // Use the server-rendered Stimulus value ({{ amount }}) as the single source of truth.
    // sessionStorage carried a stale price from the token-selection page which overwrote
    // the fresh server value, causing Alchemy Gate 1 to reject every volatile-token deposit.
    const amt = this.amountValue && this.amountValue !== "" ? this.amountValue : "0";
    const line = `${amt} ${symbol}`;
    this.amountWithSymbolTargets.forEach((el) => {
      el.textContent = line;
    });
    if (this.hasBannerSymbolTarget) {
      this.bannerSymbolTarget.textContent = symbol;
    }
    if (this.hasBannerNetworkTarget) {
      this.bannerNetworkTarget.textContent = network;
    }
    this.updateCheckoutPaymentMethod(symbol, network);
    this.updateCheckoutOverviewFromCurrentAmount();
    debugLog("DirectDeposit: applied asset from storage", { symbol, network, icon });
  }

  /**
   * Sync checkout overview total from what this page currently displays.
   * This keeps overview values consistent on direct-deposit Turbo visits and reloads,
   * without affecting non-direct-deposit pages.
   */
  updateCheckoutOverviewFromCurrentAmount() {
    if (!this.hasAmountWithSymbolTarget) return;
    const amountDisplay = (this.amountWithSymbolTarget.textContent || "").trim();
    if (!amountDisplay) return;

    const checkoutTotalAmount = document.getElementById("checkout_total_amount");
    if (checkoutTotalAmount) {
      checkoutTotalAmount.textContent = amountDisplay;
    }

    const checkoutPriceMobile = document.getElementById("checkout_price_mobile");
    if (checkoutPriceMobile) {
      checkoutPriceMobile.textContent = `${amountDisplay}`;
    }
  }

  updateCheckoutPaymentMethod(symbol, networkName) {
    const checkoutPaymentMethod = document.getElementById("checkout_payment_method");
    if (!checkoutPaymentMethod) return;

    const sym = (symbol || "").trim();
    const net = (networkName || "").trim();
    checkoutPaymentMethod.textContent =
      sym && net ? `${net} / ${sym}` : "Awaiting Selection";
  }

  toggleQr() {
    if (!this.hasQrCodeTarget) return;
    const isCurrentlyExpanded = this.qrCodeTarget.classList.contains("flex");
    this.setQrExpanded(!isCurrentlyExpanded);
  }

  changeAsset() {
    const url = `/${this.codeValue}/payment-options?session=${encodeURIComponent(this.sessionValue)}`;
    debugLog("DirectDepositController: changeAsset ->", url);
    Turbo.visit(url, { frame: "step_content", action: "advance" });
  }

  showLoadingScreen() {
    this.dispatch("showLoadingScreen", { detail: { text: "Please Wait" } });
  }

  hideLoadingScreen() {
    this.dispatch("hideLoadingScreen");
  }

  getValidatedRedirectURL(urlValue) {
    if (!urlValue) return "";
    try {
      const u = new URL(urlValue, window.location.origin);
      if (u.protocol !== "http:" && u.protocol !== "https:") return "";
      return u.toString();
    } catch {
      return "";
    }
  }

}
